import React from 'react';
import type {
    ImageCropUploadValue,
    ImageUploadPayload,
} from '@/components/imageCropUpload';
import { useResetOnKeyChange } from '@/utils';

export type BranchFormInitialValues = {
    name: string;
    address: string;
    /** Square metres as free text; blank means "unknown" (null on the wire). */
    area: string;
    isMain: boolean;
    image: ImageCropUploadValue | null;
};

/**
 * What the modal hands back when the user submits. `imageUrl` is the *existing*
 * URL on edits (or `null` when cleared); `imageUpload` is only set when the
 * user picked a fresh image that still needs to be uploaded. `area` is parsed
 * to a number (or `null` when blank) so the wire payload matches the bos-api's
 * decimal contract.
 */
export type BranchFormSubmit = {
    name: string;
    address: string;
    isMain: boolean;
    area: number | null;
    imageUpload: ImageUploadPayload | null;
    imageUrl: string | null;
};

type UseBranchFormParams = {
    initialValues?: BranchFormInitialValues;
    isOpen: boolean;
    onSubmit: (input: BranchFormSubmit) => void;
};

const EMPTY_INITIAL: BranchFormInitialValues = {
    name: '',
    address: '',
    area: '',
    isMain: false,
    image: null,
};

/**
 * Parse the free-text area into a number. Returns `null` for blank input and
 * `NaN` for anything that isn't a finite, positive number — callers gate on
 * `isAreaValid` first.
 */
export const parseArea = (area: string): number | null => {
    const trimmed = area.trim();
    if (trimmed.length === 0) {
        return null;
    }
    return Number(trimmed);
};

/**
 * Client-side guard for the optional `area` field: blank is allowed (means
 * "unknown"); a present value must be a finite number greater than zero. The
 * server re-validates (`Field 'area' must be greater than 0.`) as the source of
 * truth.
 */
export const isAreaValid = (area: string): boolean => {
    const trimmed = area.trim();
    if (trimmed.length === 0) {
        return true;
    }
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) && parsed > 0;
};

/**
 * Form state for the Add / Edit Branch modal. Mirrors `useSupplierForm`:
 * re-seed from `initialValues` on every open and close, gate submit on the
 * required fields (name + address) plus the area shape check, and carry the
 * cropper payload through so the table hook can drive the upload. The `isMain`
 * switch is the branch-specific control.
 */
export const useBranchForm = ({
    initialValues,
    isOpen,
    onSubmit,
}: UseBranchFormParams) => {
    const initial = initialValues ?? EMPTY_INITIAL;

    const [image, setImage] = React.useState<ImageCropUploadValue | null>(
        initial.image,
    );
    const [name, setName] = React.useState(initial.name);
    const [address, setAddress] = React.useState(initial.address);
    const [area, setArea] = React.useState(initial.area);
    const [isMain, setIsMain] = React.useState(initial.isMain);
    const [areaTouched, setAreaTouched] = React.useState(false);

    // Re-seed every field from initialValues on each open/close transition —
    // see useCategoryForm. The modal is always mounted and the page opens Edit
    // and sets the record in one update, so seeding on open is what fills the
    // branch's values; seeding on close clears an Add draft. Keyed on `isOpen`
    // only, so a background refetch mid-edit doesn't clobber the user's typing.
    useResetOnKeyChange(isOpen, () => {
        setImage(initial.image);
        setName(initial.name);
        setAddress(initial.address);
        setArea(initial.area);
        setIsMain(initial.isMain);
        setAreaTouched(false);
    });

    const onNameChange = React.useCallback((value: string) => {
        setName(value);
    }, []);

    const onAddressChange = React.useCallback((value: string) => {
        setAddress(value);
    }, []);

    const onAreaChange = React.useCallback((value: string) => {
        setArea(value);
        setAreaTouched(true);
    }, []);

    const onAreaBlur = React.useCallback(() => {
        setAreaTouched(true);
    }, []);

    const onIsMainChange = React.useCallback((value: boolean) => {
        setIsMain(value);
    }, []);

    const onImageChange = React.useCallback(
        (value: ImageCropUploadValue | null) => {
            setImage(value);
        },
        [],
    );

    const trimmedName = name.trim();
    const trimmedAddress = address.trim();
    const areaLooksValid = isAreaValid(area);
    const isValid =
        trimmedName.length > 0 && trimmedAddress.length > 0 && areaLooksValid;
    // Surface the inline error only once the user has touched the field.
    const showAreaError = areaTouched && !areaLooksValid;

    // We deliberately do NOT close the modal here — the parent's submit handler
    // owns the close decision (close on success, keep open on failure so the
    // inline `formError` is visible).
    const onSubmitClick = React.useCallback(() => {
        if (!isValid) {
            return;
        }
        onSubmit({
            name: trimmedName,
            address: trimmedAddress,
            isMain,
            area: parseArea(area),
            imageUpload: image?.imageUpload ?? null,
            imageUrl: image?.imageUpload ? null : (image?.imageUrl ?? null),
        });
    }, [area, image, isMain, isValid, onSubmit, trimmedAddress, trimmedName]);

    return {
        image,
        isValid,
        name,
        address,
        area,
        isMain,
        showAreaError,
        onImageChange,
        onNameChange,
        onAddressChange,
        onAreaChange,
        onAreaBlur,
        onIsMainChange,
        onSubmit: onSubmitClick,
    };
};
